import React from 'react';

import ListControls from '@sharedCR/common/ListControls';
import {
  getAddressFieldsConfig,
  getPropertyMortgagesConfig,
} from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { isEqual } from 'lodash';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  ADDRESS_COUNTRY_TYPE,
  BaseStatus,
  MAX_OTHER_EXPENSES_NUMBER,
  MIN_OTHER_EXPENSES_NUMBER,
} from 'constants/loanApplication';
import { MAX_NUMBER_VALUE } from 'constants/number';
import {
  baseStatusOptions,
  occupancyOptions,
  propertyStatusOptions,
  rentCalculatedPurposeOptions,
  reoPropertyTypeOptions,
} from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { ownersFieldCustomStyles } from '../../Content.styles';
import { getPlaceholder } from '../SectionContent.utils';

import { associationFeesStyles, extendableRadioStyles, propertyStyles } from './Reo.styles';

const isBorrowerModal = true;

export const getConfigFields = ({ recordId, ownersOptions, isNewRecord, isSubjectProperty }) => ({
  ...getAddressFieldsConfig({
    prefix: `[${recordId}].address`,
    isBorrowerModal,
    isPlaceHolder: isNewRecord,
  }),
  owners: {
    noValidate: isSubjectProperty,
    fieldId: 'owners',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].owners`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Choose Borrower'),
      disabled: false,
      required: true,
      multiple: true,
      variant: 'outlined',
      isOptionEqualToValue: (option, value) => isEqual(option, value),
      options: ownersOptions,
      'data-test-value': null,
      columns: 4,
      containerCustomStyles: ownersFieldCustomStyles,
      isOneRow: true,
    },
  },
  propertyType: {
    noValidate: isSubjectProperty,
    fieldId: 'propertyType',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].propertyType`,
    inputProps: {
      options: reoPropertyTypeOptions,
      placeholder: getPlaceholder(isNewRecord, 'Property Type'),
      label: 'Property Type',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  monthlyPropertyTaxes: {
    noValidate: isSubjectProperty,
    fieldId: 'monthlyPropertyTaxes',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthlyPropertyTaxes`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Monthly Property Taxes'),
      label: 'Monthly Property Taxes',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  monthlyHomeownersInsurance: {
    noValidate: isSubjectProperty,
    fieldId: 'monthlyHomeownersInsurance',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthlyHomeownersInsurance`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Monthly Homeowners Insurance'),
      label: 'Monthly Homeowners Insurance',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  monthlyPmi: {
    noValidate: isSubjectProperty,
    fieldId: 'monthlyPmi',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthlyPmi`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Monthly PMI'),
      label: 'Monthly PMI',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  associationFees: {
    noValidate: isSubjectProperty,
    fieldId: 'associationFees',
    type: FieldType.EXTENDABLE_RADIO,
    nameInModel: `[${recordId}].associationFees`,
    inputProps: {
      label: 'Association Fee',
      disabled: false,
      required: true,
      options: baseStatusOptions,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.END,
      columns: 4,
    },
  },
  hasOtherExpenses: {
    noValidate: isSubjectProperty,
    fieldId: 'hasOtherExpenses',
    type: FieldType.EXTENDABLE_RADIO,
    nameInModel: `[${recordId}].hasOtherExpenses`,
    inputProps: {
      label: 'Other Expenses',
      disabled: false,
      required: true,
      options: baseStatusOptions,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.END,
      columns: 4,
      className: 'override-w-full flex-row items-center',
    },
  },
  otherExpensesName: {
    noValidate: isSubjectProperty,
    fieldId: 'otherExpensesName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].otherExpenses[0].name`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Other Expenses Name'),
      label: 'Other Expenses Name',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  otherExpensesValue: {
    noValidate: isSubjectProperty,
    fieldId: 'otherExpensesValue',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].otherExpenses[0].value`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Other Expenses Amount'),
      label: 'Other Expenses Amount',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  associationFeesAmount: {
    noValidate: isSubjectProperty,
    fieldId: 'associationFeesAmount',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].associationFeesAmount`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Monthly Fee'),
      label: 'Monthly Fee',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  associationFeesName: {
    noValidate: isSubjectProperty,
    fieldId: 'associationFeesName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].associationFeesName`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Association Name'),
      label: 'Association Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  associationFeesEmail: {
    noValidate: isSubjectProperty,
    fieldId: 'associationFeesEmail',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].associationFeesEmail`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Association Email'),
      label: 'Association Email',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.EMAIL,
    },
  },
  associationFeesPhoneNumber: {
    noValidate: isSubjectProperty,
    fieldId: 'associationFeesPhoneNumber',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].associationFeesPhoneNumber`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Association Phone Number'),
      label: 'Association Phone Number',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
    },
  },
  taxesEscrowed: {
    noValidate: isSubjectProperty,
    fieldId: 'taxesEscrowed',
    type: FieldType.RADIO,
    nameInModel: `[${recordId}].taxesEscrowed`,
    inputProps: {
      label: 'Taxes are Escrowed',
      disabled: false,
      required: true,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.END,
      options: baseStatusOptions,
      columns: 4,
      className: 'override-w-full flex-row items-center',
    },
  },
  homeownersInsuranceEscrowed: {
    noValidate: isSubjectProperty,
    fieldId: 'homeownersInsuranceEscrowed',
    type: FieldType.RADIO,
    nameInModel: `[${recordId}].homeownersInsuranceEscrowed`,
    inputProps: {
      label: 'Homeowners Insurance is Escrowed',
      disabled: false,
      required: true,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.END,
      options: baseStatusOptions,
      columns: 4,
      className: 'override-w-full flex-row items-center',
    },
  },
  propertyValue: {
    noValidate: isSubjectProperty,
    fieldId: 'propertyValue',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].propertyValue`,
    inputProps: {
      placeholder: getPlaceholder(isNewRecord, 'Current Market Value'),
      label: 'Current Market Value',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  status: {
    noValidate: isSubjectProperty,
    fieldId: 'status',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].status`,
    inputProps: {
      options: propertyStatusOptions,
      placeholder: getPlaceholder(isNewRecord, 'Property Status'),
      label: 'Property Status',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  occupancy: {
    noValidate: isSubjectProperty,
    fieldId: 'occupancy',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].occupancy`,
    inputProps: {
      options: occupancyOptions,
      placeholder: getPlaceholder(isNewRecord, 'Intended Occupancy'),
      label: 'Intended Occupancy',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  rentCalculatedBasedOn: {
    noValidate: isSubjectProperty,
    fieldId: 'rentCalculatedBasedOn',
    type: FieldType.RADIO,
    nameInModel: `[${recordId}].rentCalculatedBasedOn`,
    inputProps: {
      label: 'Rent calculated based on',
      disabled: false,
      required: false,
      options: rentCalculatedPurposeOptions,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.START,
      columns: 4,
    },
  },
  monthlyRentalIncome: {
    noValidate: isSubjectProperty,
    fieldId: 'monthlyRentalIncome',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthlyRentalIncome`,
    inputProps: {
      label: 'Gross Rent',
      placeholder: getPlaceholder(isNewRecord, 'Gross Rent'),
      required: false,
      variant: 'outlined',
      minValue: 0,
      type: FieldInputType.CURRENCY,
    },
  },
  mortgagesPayment: {
    fieldId: 'mortgagesPayment',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].mortgagesPayment`,
    noValidate: true,
    noObserve: true,
    inputProps: {
      label: 'Mortgages Payment(s)',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      maxValue: 2 * MAX_NUMBER_VALUE,
    },
  },
  expenses: {
    fieldId: 'expenses',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].expenses`,
    noObserve: true,
    noValidate: true,
    inputProps: {
      label: 'Expenses',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
  netRent: {
    fieldId: 'netRent',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].netMonthlyRentalIncome`,
    noValidate: true,
    noObserve: true,
    inputProps: {
      label: 'Net Rent',
      disabled: true,
      required: false,
      allowNegative: true,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
    },
  },
});

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    recordId,
    associationFees,
    hasOtherExpenses,
    otherExpensesCount,
    onAddOtherExpenses,
    onOtherExpensesDelete,
    firstMortgageOptions,
    secondMortgageOptions,
    isAmountOfRentCollectedActive = false,
    isFirstMortgageHeloc,
    isSecondMortgageHeloc,
    isSoldPropertyStatus,
    hasFirstMortgage,
    hasSecondMortgage,
    taxesEscrowed,
    homeownersInsuranceEscrowed,
    isMonthlyPmiOrHomeownersInsuranceFilled,
    counties = {},
    state,
    ownersOptions,
    onHasOtherExpensesChange,
    country,
    isNewRecord,
    isOwnersClicked,
    onLiabilityChange,
    isSubjectProperty,
  }) => {
    const hasOtherExpensesFields = !!(hasOtherExpenses && otherExpensesCount);
    const isEscrowDisabled = !hasFirstMortgage;

    const filteredCounties = state ? counties[state] : counties.ALL_COUNTIES;
    const isAddingOtherExpensesPossible = otherExpensesCount < MAX_OTHER_EXPENSES_NUMBER;
    const isWithCountry = country === ADDRESS_COUNTRY_TYPE.USA;

    const addressFields = Object.values(
      getAddressFieldsConfig({
        prefix: `[${recordId}].address`,
        filteredCounties,
        isBorrowerModal,
        isWithCountry,
        isPlaceHolder: isNewRecord,
      }),
    );

    const configFields = getConfigFields({
      recordId,
      ownersOptions,
      isNewRecord,
      isSubjectProperty,
    });

    return {
      groups: [
        ...getConditionalArray(isOwnersClicked || isNewRecord, [
          {
            type: FieldType.GROUP,
            fields: [configFields.owners],
            key: `${recordId}-owners`,
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: addressFields,
          key: `${recordId}-address`,
          lockAsGroup: true,
        },
        {
          type: FieldType.GROUP,
          key: `${recordId}-property`,
          customStyles: propertyStyles,
          fields: [
            configFields.propertyType,
            configFields.propertyValue,
            configFields.status,
            {
              ...configFields.occupancy,
              inputProps: {
                ...configFields.occupancy.inputProps,
                required: !isSoldPropertyStatus,
              },
            },
          ],
        },
        {
          type: FieldType.GROUP,
          key: `${recordId}-escrowedRadios`,
          fields: [
            {
              ...configFields.taxesEscrowed,
              inputProps: {
                ...configFields.taxesEscrowed.inputProps,
                disabled: isEscrowDisabled,
                required: !isEscrowDisabled,
              },
            },
            {
              ...configFields.homeownersInsuranceEscrowed,
              inputProps: {
                ...configFields.homeownersInsuranceEscrowed.inputProps,
                disabled: isEscrowDisabled,
                required: !isEscrowDisabled,
              },
            },
          ],
        },
        {
          type: FieldType.GROUP,
          key: `${recordId}-monthly`,
          fields: [
            {
              ...configFields.monthlyPropertyTaxes,
              inputProps: {
                ...configFields.monthlyPropertyTaxes.inputProps,
                required: !taxesEscrowed,
              },
            },
            {
              ...configFields.monthlyHomeownersInsurance,
              inputProps: {
                ...configFields.monthlyHomeownersInsurance.inputProps,
                required: !homeownersInsuranceEscrowed && !isMonthlyPmiOrHomeownersInsuranceFilled,
              },
            },
            {
              ...configFields.monthlyPmi,
              inputProps: {
                ...configFields.monthlyPmi.inputProps,
                required: !homeownersInsuranceEscrowed && !isMonthlyPmiOrHomeownersInsuranceFilled,
              },
            },
          ],
        },
        {
          type: FieldType.GROUP,
          customStyles: extendableRadioStyles,
          key: `${recordId}-associationFees`,
          fields: [
            {
              ...configFields.associationFees,
              additionalContentBySelectedOption: {
                [BaseStatus.YES]: {
                  groups: [
                    {
                      type: FieldType.GROUP,
                      customStyles: associationFeesStyles,
                      fields: [
                        {
                          ...configFields.associationFeesAmount,
                          inputProps: {
                            ...configFields.associationFeesAmount.inputProps,
                            required: associationFees,
                          },
                        },
                      ],
                    },
                    {
                      type: FieldType.GROUP,
                      customStyles: { gridTemplateColumns: `repeat(4, 1fr)` },
                      fields: [
                        configFields.associationFeesName,
                        configFields.associationFeesEmail,
                        configFields.associationFeesPhoneNumber,
                      ],
                    },
                  ],
                },
              },
            },
          ],
        },
        {
          type: FieldType.GROUP,
          key: `${recordId}-hasOtherExpenses`,
          customStyles: extendableRadioStyles,
          fields: [
            {
              ...configFields.hasOtherExpenses,
              inputProps: {
                ...configFields.hasOtherExpenses.inputProps,
                customOnChange: onHasOtherExpensesChange,
              },
              additionalContentBySelectedOption: {
                [BaseStatus.YES]: {
                  groups: !hasOtherExpensesFields
                    ? []
                    : Array.from({ length: otherExpensesCount || 1 }).map((_, index) => {
                        return {
                          type: FieldType.GROUP,
                          key: `${recordId}-otherExpenses-${index}`,
                          customStyles: associationFeesStyles,
                          customControls: (
                            <ListControls
                              isAddVisible={isAddingOtherExpensesPossible}
                              isRemoveVisible={otherExpensesCount > MIN_OTHER_EXPENSES_NUMBER}
                              onAdd={onAddOtherExpenses}
                              onRemove={onOtherExpensesDelete?.(index)}
                            />
                          ),
                          fields: [
                            {
                              ...configFields.otherExpensesName,
                              nameInModel: configFields.otherExpensesName.nameInModel.replace(
                                '[0]',
                                `[${index}]`,
                              ),
                            },
                            {
                              ...configFields.otherExpensesValue,
                              nameInModel: configFields.otherExpensesValue.nameInModel.replace(
                                '[0]',
                                `[${index}]`,
                              ),
                            },
                          ],
                        };
                      }),
                },
              },
            },
          ],
        },
        ...getPropertyMortgagesConfig({
          isBorrowerModal: true,
          extendableRadioStyles,
          prefix: `[${recordId}]`,
          key: recordId,
          firstMortgageOptions,
          secondMortgageOptions,
          hasFirstMortgage,
          hasSecondMortgage,
          isFirstMortgageHeloc,
          isSecondMortgageHeloc,
          isSoldPropertyStatus,
          onLiabilityChange,
        }),
        ...getConditionalArray(isAmountOfRentCollectedActive, [
          {
            type: FieldType.GROUP,
            key: `${recordId}-isAmountOfRentCollectedActive`,
            fields: [
              configFields.rentCalculatedBasedOn,
              configFields.monthlyRentalIncome,
              configFields.mortgagesPayment,
              configFields.expenses,
              configFields.netRent,
            ],
          },
        ]),
      ],
    };
  },
);
